import React, { useState, useEffect } from 'react';
import { getAIInsights, getCostForecast, getSeasonality, getDashboard, getDrilldown, getClustering, getAnomalies, getRecommendations, getOptimizationPlan, getProductionInsights } from '../api/api';
import AIInsightsWidget from '../components/AIInsightsWidget';
import AIChatWidget from '../components/AIChatWidget';
import {
    Box, Typography, Paper, Tabs, Tab, Grid, Alert, Stack, Chip, LinearProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Divider,
    Accordion, AccordionSummary, AccordionDetails, Stepper, Step, StepLabel, StepContent
} from '@mui/material';
import {
    SmartToy as SmartToyIcon, Assessment as AssessmentIcon, Chat as ChatIcon,
    Lightbulb as LightbulbIcon, TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon,
    Timeline as TimelineIcon, ShowChart as ShowChartIcon, CalendarMonth as CalendarIcon,
    Dashboard as DashboardIcon, PieChart as PieChartIcon, Close as CloseIcon,
    Psychology as PsychologyIcon, BubbleChart as BubbleChartIcon, Warning as WarningIcon,
    Recommend as RecommendIcon, ExpandMore as ExpandMoreIcon, AttachMoney as MoneyIcon,
    PlaylistAddCheck as PlanIcon, Flag as FlagIcon, TrendingFlat as FlatIcon
} from '@mui/icons-material';

const AIPage = () => {
    const [aiInsights, setAiInsights] = useState([]);
    const [forecast, setForecast] = useState(null);
    const [seasonality, setSeasonality] = useState(null);
    const [dashboard, setDashboard] = useState(null);
    const [clustering, setClustering] = useState(null);
    const [anomalies, setAnomalies] = useState(null);
    const [recommendations, setRecommendations] = useState(null);
    const [optimizationPlan, setOptimizationPlan] = useState(null);
    const [loadingForecast, setLoadingForecast] = useState(false);
    const [loadingSeasonality, setLoadingSeasonality] = useState(false);
    const [loadingDashboard, setLoadingDashboard] = useState(false);
    const [loadingAdvanced, setLoadingAdvanced] = useState(false);
    const [loadingPlan, setLoadingPlan] = useState(false);
    const [productionInsights, setProductionInsights] = useState([]);
    const [loadingProduction, setLoadingProduction] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const [drilldownOpen, setDrilldownOpen] = useState(false);
    const [drilldownData, setDrilldownData] = useState(null);
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [selectedCentro, setSelectedCentro] = useState('');

    useEffect(() => {
        getAIInsights().then(res => res.data?.insights && setAiInsights(res.data.insights)).catch(console.error);
    }, []);

    useEffect(() => {
        if (activeTab === 0) {
            if (!forecast && !loadingForecast) {
                setLoadingForecast(true);
                getCostForecast()
                    .then(res => setForecast(res.data || { status: 'error' }))
                    .catch(err => { console.error(err); setForecast({ status: 'error' }); })
                    .finally(() => setLoadingForecast(false));
            }
            if (!seasonality && !loadingSeasonality) {
                setLoadingSeasonality(true);
                getSeasonality()
                    .then(res => setSeasonality(res.data || { status: 'error' }))
                    .catch(err => { console.error(err); setSeasonality({ status: 'error' }); })
                    .finally(() => setLoadingSeasonality(false));
            }
        }
        if (activeTab === 1 && !dashboard && !loadingDashboard) {
            setLoadingDashboard(true);
            getDashboard()
                .then(res => setDashboard(res.data || { status: 'error' }))
                .catch(err => { console.error(err); setDashboard({ status: 'error' }); })
                .finally(() => setLoadingDashboard(false));
        }
        if (activeTab === 2 && !clustering && !loadingAdvanced) {
            setLoadingAdvanced(true);
            Promise.all([getClustering(), getAnomalies(), getRecommendations()])
                .then(([clusterRes, anomalyRes, recRes]) => {
                    setClustering(clusterRes.data || { status: 'error' });
                    setAnomalies(anomalyRes.data || { status: 'error' });
                    setRecommendations(recRes.data || { status: 'error' });
                })
                .catch(err => {
                    console.error(err);
                    setClustering({ status: 'error' });
                    setAnomalies({ status: 'error' });
                    setRecommendations({ status: 'error' });
                })
                .finally(() => setLoadingAdvanced(false));
        }
        if (activeTab === 3 && !optimizationPlan && !loadingPlan) {
            setLoadingPlan(true);
            getOptimizationPlan()
                .then(res => setOptimizationPlan(res.data || { status: 'error' }))
                .catch(err => { console.error(err); setOptimizationPlan({ status: 'error' }); })
                .finally(() => setLoadingPlan(false));
        }
        if (activeTab === 4 && productionInsights.length === 0 && !loadingProduction) {
            setLoadingProduction(true);
            getProductionInsights()
                .then(res => setProductionInsights(res.data?.insights || []))
                .catch(console.error)
                .finally(() => setLoadingProduction(false));
        }
    }, [activeTab, forecast, loadingForecast, seasonality, loadingSeasonality, dashboard, loadingDashboard, clustering, loadingAdvanced, optimizationPlan, loadingPlan, productionInsights.length, loadingProduction]);

    const handleOpenDrilldown = async (centro) => {
        setSelectedCentro(centro); setDrilldownOpen(true); setDrilldownLoading(true);
        try { const res = await getDrilldown(centro); res.data?.status === 'success' && setDrilldownData(res.data); }
        catch (e) { console.error(e); }
        setDrilldownLoading(false);
    };

    const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
    const getTrendIcon = (t) => t > 0 ? <TrendingUpIcon color="error" /> : t < 0 ? <TrendingDownIcon color="success" /> : <FlatIcon color="info" />;
    const getEfficiencyColor = (e) => ({ 'alta': '#4caf50', 'media': '#ff9800', 'baixa': '#f44336' }[e] || '#9e9e9e');
    const getPriorityColor = (p) => ({ 'critica': 'error', 'alta': 'warning', 'media': 'info', 'baixa': 'success' }[p] || 'default');
    const getSeverityColor = (s) => ({ 'alta': '#f44336', 'media': '#ff9800', 'baixa': '#4caf50' }[s] || '#9e9e9e');

    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <SmartToyIcon sx={{ mr: 2, color: 'primary.main' }} /> Assistente de Inteligência (IA)
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Análises avançadas com machine learning e estatística para otimização de custos.
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
                    <Tab icon={<AssessmentIcon />} iconPosition="start" label="Análise" />
                    <Tab icon={<DashboardIcon />} iconPosition="start" label="Dashboard" />
                    <Tab icon={<PsychologyIcon />} iconPosition="start" label="IA Avançada" />
                    <Tab icon={<PlanIcon />} iconPosition="start" label="Plano de Otimização" />
                    <Tab icon={<PieChartIcon />} iconPosition="start" label="Análise de Produção" />
                    <Tab icon={<ChatIcon />} iconPosition="start" label="Chat" />
                </Tabs>
            </Box>

            {/* Tab 0: Análise */}
            {activeTab === 0 && (
                <Stack spacing={4}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'primary.main', mb: 2 }}>
                            <ShowChartIcon sx={{ mr: 1 }} /> Previsão de Custos
                        </Typography>
                        {loadingForecast ? <LinearProgress /> : forecast?.status === 'success' ? (
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}><Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>{getTrendIcon(forecast.metricas?.tendencia_percentual)}<Typography variant="subtitle2" color="text.secondary">Tendência</Typography></Stack>
                                    <Typography variant="h5" fontWeight="bold">{forecast.metricas?.tendencia_texto || 'N/A'}</Typography>
                                    <Typography variant="body2" color="text.secondary">{forecast.metricas?.tendencia_percentual > 0 ? '+' : ''}{forecast.metricas?.tendencia_percentual?.toFixed(1)}% ao mês</Typography>
                                </Paper></Grid>
                                <Grid item xs={12} md={4}><Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">Próximo Mês</Typography>
                                    <Typography variant="h5" fontWeight="bold" color="primary.main">{forecast.previsoes?.[0] ? formatCurrency(forecast.previsoes[0].valor_previsto) : 'N/A'}</Typography>
                                </Paper></Grid>
                                <Grid item xs={12} md={4}><Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary">Confiabilidade</Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="h5" fontWeight="bold">{forecast.metricas?.confiabilidade || 'N/A'}</Typography>
                                        <Chip label={`R² = ${(forecast.metricas?.r_squared * 100)?.toFixed(0)}%`} size="small" color={forecast.metricas?.r_squared > 0.7 ? 'success' : 'warning'} />
                                    </Stack>
                                </Paper></Grid>
                            </Grid>
                        ) : <Alert severity="warning">Dados insuficientes para previsões.</Alert>}
                    </Paper>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'primary.main', mb: 2 }}>
                            <CalendarIcon sx={{ mr: 1 }} /> Sazonalidade
                        </Typography>
                        {loadingSeasonality ? <LinearProgress /> : seasonality?.status === 'success' ? (
                            <Grid container spacing={2}>
                                <Grid item xs={4}><Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography variant="subtitle2" color="text.secondary">Mês Pico</Typography>
                                    <Typography variant="h6" fontWeight="bold" color="error.main">{seasonality.resumo?.mes_pico || 'N/A'}</Typography>
                                </Paper></Grid>
                                <Grid item xs={4}><Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography variant="subtitle2" color="text.secondary">Mês Vale</Typography>
                                    <Typography variant="h6" fontWeight="bold" color="success.main">{seasonality.resumo?.mes_vale || 'N/A'}</Typography>
                                </Paper></Grid>
                                <Grid item xs={4}><Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography variant="subtitle2" color="text.secondary">Amplitude</Typography>
                                    <Typography variant="h6" fontWeight="bold">{formatCurrency(seasonality.resumo?.amplitude_sazonal || 0)}</Typography>
                                </Paper></Grid>
                            </Grid>
                        ) : <Alert severity="warning">Dados insuficientes.</Alert>}
                    </Paper>
                    <Box>
                        <Typography variant="h6" fontWeight="bold" mb={2}>Destaques Estatísticos</Typography>
                        <Grid container spacing={2}>
                            {aiInsights.slice(0, 9).map((insight, idx) => (
                                <Grid item xs={12} sm={6} lg={4} key={idx}>
                                    <Paper elevation={1} onClick={() => insight.centro !== 'Geral' && handleOpenDrilldown(insight.centro)}
                                        sx={{ p: 2, borderLeft: 4, borderColor: insight.tipo === 'Alerta' ? 'error.main' : 'info.main', cursor: insight.centro !== 'Geral' ? 'pointer' : 'default', '&:hover': { boxShadow: 3 } }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                            <Typography variant="overline" color={insight.tipo === 'Alerta' ? 'error.main' : 'info.main'}>{insight.categoria}</Typography>
                                            <Chip label={insight.centro} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                        </Stack>
                                        <Typography variant="body2" fontWeight={600}>{insight.mensagem}</Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Stack>
            )}

            {/* Tab 1: Dashboard */}
            {activeTab === 1 && (
                loadingDashboard ? <LinearProgress /> : dashboard?.status === 'success' ? (
                    <Stack spacing={4}>
                        <Grid container spacing={2}>
                            {[{ label: 'Custo Total', value: formatCurrency(dashboard.resumo?.total_custo), color: 'error' },
                            { label: 'Receita Total', value: formatCurrency(dashboard.resumo?.total_receita), color: 'success' },
                            { label: 'Margem Geral', value: `${dashboard.resumo?.margem_geral}%`, color: dashboard.resumo?.margem_geral > 0 ? 'info' : 'warning' },
                            { label: 'Centros', value: dashboard.resumo?.qtd_centros, color: 'grey' }
                            ].map((card, i) => (
                                <Grid item xs={6} md={3} key={i}>
                                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: `${card.color}.light`, borderRadius: 2 }}>
                                        <Typography variant="h5" fontWeight="bold">{card.value}</Typography>
                                        <Typography variant="body2">{card.label}</Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" fontWeight="bold" mb={2}><PieChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />Custo vs Receita</Typography>
                            <TableContainer><Table size="small">
                                <TableHead><TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Centro</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Custo</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Receita</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Margem</TableCell>
                                </TableRow></TableHead>
                                <TableBody>{dashboard.heatmap?.map((row, idx) => (
                                    <TableRow key={idx} hover onClick={() => handleOpenDrilldown(row.centro)} sx={{ cursor: 'pointer' }}>
                                        <TableCell>{row.centro}</TableCell>
                                        <TableCell align="right" sx={{ color: 'error.main', fontFamily: 'monospace' }}>{formatCurrency(row.custo)}</TableCell>
                                        <TableCell align="right" sx={{ color: 'success.main', fontFamily: 'monospace' }}>{formatCurrency(row.receita)}</TableCell>
                                        <TableCell align="right"><Chip size="small" label={`${row.margem}%`} sx={{ bgcolor: getEfficiencyColor(row.eficiencia), color: 'white' }} /></TableCell>
                                    </TableRow>
                                ))}</TableBody>
                            </Table></TableContainer>
                        </Paper>
                    </Stack>
                ) : <Alert severity="warning">Sem dados para dashboard.</Alert>
            )}

            {/* Tab 2: IA Avançada */}
            {activeTab === 2 && (
                loadingAdvanced ? <Box sx={{ py: 8 }}><LinearProgress /></Box> : (
                    <Stack spacing={4}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'primary.main', mb: 2 }}>
                                <RecommendIcon sx={{ mr: 1 }} /> Recomendações
                                {recommendations?.economia_potencial_total > 0 && <Chip label={`Economia: ${formatCurrency(recommendations.economia_potencial_total)}`} color="success" size="small" sx={{ ml: 2 }} />}
                            </Typography>
                            {recommendations?.status === 'success' && recommendations?.recomendacoes?.length > 0 ? (
                                <Stack spacing={1}>{recommendations.recomendacoes.slice(0, 5).map((rec, idx) => (
                                    <Accordion key={idx} disableGutters>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                                                <Chip label={rec.prioridade.toUpperCase()} size="small" color={getPriorityColor(rec.prioridade)} />
                                                <Typography fontWeight="bold" sx={{ flex: 1 }}>{rec.titulo}</Typography>
                                                {rec.economia_potencial > 0 && <Chip icon={<MoneyIcon />} label={formatCurrency(rec.economia_potencial)} size="small" variant="outlined" color="success" />}
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2"><strong>Descrição:</strong> {rec.descricao}</Typography>
                                            <Typography variant="body2"><strong>Ação:</strong> {rec.acao}</Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}</Stack>
                            ) : <Alert severity="info">Nenhuma recomendação identificada.</Alert>}
                        </Paper>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'primary.main', mb: 2 }}>
                                <WarningIcon sx={{ mr: 1 }} /> Anomalias
                                {anomalies?.por_severidade?.alta > 0 && <Chip label={`${anomalies.por_severidade.alta} críticas`} size="small" color="error" sx={{ ml: 2 }} />}
                            </Typography>
                            {anomalies?.status === 'success' && anomalies?.anomalias?.length > 0 ? (
                                <TableContainer sx={{ maxHeight: 250 }}><Table size="small" stickyHeader>
                                    <TableHead><TableRow sx={{ bgcolor: 'action.hover' }}>
                                        <TableCell>Tipo</TableCell><TableCell>Centro</TableCell><TableCell>Detalhes</TableCell><TableCell>Severidade</TableCell>
                                    </TableRow></TableHead>
                                    <TableBody>{anomalies.anomalias.slice(0, 8).map((a, idx) => (
                                        <TableRow key={idx} hover onClick={() => handleOpenDrilldown(a.centro)} sx={{ cursor: 'pointer' }}>
                                            <TableCell><Chip label={a.tipo} size="small" variant="outlined" /></TableCell>
                                            <TableCell>{a.centro}</TableCell>
                                            <TableCell><Typography variant="caption">{a.desvio_percentual ? `${a.desvio_percentual}%` : formatCurrency(a.valor || 0)}</Typography></TableCell>
                                            <TableCell><Chip label={a.severidade} size="small" sx={{ bgcolor: getSeverityColor(a.severidade), color: 'white' }} /></TableCell>
                                        </TableRow>
                                    ))}</TableBody>
                                </Table></TableContainer>
                            ) : <Alert severity="success">Nenhuma anomalia detectada.</Alert>}
                        </Paper>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'primary.main', mb: 2 }}>
                                <BubbleChartIcon sx={{ mr: 1 }} /> Clusters
                            </Typography>
                            {clustering?.status === 'success' && clustering?.clusters?.length > 0 ? (
                                <Grid container spacing={2}>{clustering.clusters.map((cluster, idx) => (
                                    <Grid item xs={12} md={4} key={idx}>
                                        <Paper elevation={2} sx={{ p: 2, borderTop: 4, borderColor: cluster.cor === 'success' ? 'success.main' : cluster.cor === 'warning' ? 'warning.main' : 'error.main', borderRadius: 2 }}>
                                            <Typography variant="subtitle1" fontWeight="bold">{cluster.nome}</Typography>
                                            <Typography variant="body2" color="text.secondary">{cluster.perfil}</Typography>
                                            <Typography variant="caption">Centros: {cluster.qtd_centros} | Margem: {cluster.margem_media}%</Typography>
                                        </Paper>
                                    </Grid>
                                ))}</Grid>
                            ) : <Alert severity="info">Dados insuficientes para clusterização.</Alert>}
                        </Paper>
                    </Stack>
                )
            )}

            {/* Tab 3: Plano de Otimização */}
            {activeTab === 3 && (
                loadingPlan ? <Box sx={{ py: 8 }}><LinearProgress /><Typography textAlign="center" mt={2}>Gerando plano de otimização...</Typography></Box> : optimizationPlan?.status === 'success' ? (
                    <Stack spacing={4}>
                        {/* Resumo Executivo */}
                        <Paper sx={{ p: 3, borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                            <Typography variant="h5" fontWeight="bold" mb={2}>Resumo Executivo</Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="h4" fontWeight="bold">{formatCurrency(optimizationPlan.resumo_executivo?.impacto_potencial_total)}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Economia Potencial Total</Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="h4" fontWeight="bold">{optimizationPlan.resumo_executivo?.total_planos}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Planos de Ação</Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="h4" fontWeight="bold">{optimizationPlan.resumo_executivo?.margem_atual}%</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Margem Atual</Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {getTrendIcon(optimizationPlan.tendencia_custos?.alerta ? 1 : -1)}
                                        <Box>
                                            <Typography variant="h6" fontWeight="bold">{optimizationPlan.tendencia_custos?.direcao}</Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.9 }}>Tendência de Custos</Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Planos Detalhados */}
                        {optimizationPlan.planos?.map((plano, idx) => (
                            <Paper key={idx} sx={{ p: 3, borderRadius: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                                    <FlagIcon sx={{ color: plano.prioridade === 'critica' ? 'error.main' : plano.prioridade === 'alta' ? 'warning.main' : 'info.main' }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="h6" fontWeight="bold">{plano.titulo}</Typography>
                                            <Chip label={plano.prioridade.toUpperCase()} size="small" color={getPriorityColor(plano.prioridade)} />
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">{plano.categoria} • {plano.resumo}</Typography>
                                    </Box>
                                    <Chip icon={<MoneyIcon />} label={formatCurrency(plano.impacto_financeiro)} color="success" />
                                </Stack>

                                <Accordion defaultExpanded={idx === 0}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">Diagnóstico</Typography></AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            {Object.entries(plano.diagnostico).map(([key, value]) => (
                                                <Grid item xs={12} md={4} key={key}>
                                                    <Typography variant="caption" color="text.secondary" textTransform="capitalize">{key.replace('_', ' ')}</Typography>
                                                    <Typography variant="body2">{value}</Typography>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">Cenários de Implementação</Typography></AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            {plano.cenarios?.map((cenario, cidx) => (
                                                <Grid item xs={12} md={4} key={cidx}>
                                                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight="bold">{cenario.nome}</Typography>
                                                        <Typography variant="body2" color="text.secondary" mb={1}>{cenario.descricao}</Typography>
                                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                                            <Chip label={`Economia: ${formatCurrency(cenario.economia)}`} size="small" color="success" variant="outlined" />
                                                            <Chip label={`Prazo: ${cenario.prazo}`} size="small" variant="outlined" />
                                                            <Chip label={`Sucesso: ${cenario.probabilidade_sucesso}`} size="small" variant="outlined" />
                                                        </Stack>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">Plano de Ação ({plano.plano_acao?.length} etapas)</Typography></AccordionSummary>
                                    <AccordionDetails>
                                        <Stepper orientation="vertical">
                                            {plano.plano_acao?.map((step, sidx) => (
                                                <Step key={sidx} active>
                                                    <StepLabel>
                                                        <Typography>{step.acao}</Typography>
                                                        <Typography variant="caption" color="text.secondary">Responsável: {step.responsavel} • Prazo: {step.prazo}</Typography>
                                                    </StepLabel>
                                                </Step>
                                            ))}
                                        </Stepper>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight="bold">KPIs de Monitoramento</Typography></AccordionSummary>
                                    <AccordionDetails>
                                        <TableContainer><Table size="small">
                                            <TableHead><TableRow sx={{ bgcolor: 'action.hover' }}><TableCell>Indicador</TableCell><TableCell>Meta</TableCell><TableCell>Frequência</TableCell></TableRow></TableHead>
                                            <TableBody>{plano.kpis_monitoramento?.map((kpi, kidx) => (
                                                <TableRow key={kidx}><TableCell>{kpi.indicador}</TableCell><TableCell><Chip label={kpi.meta} size="small" color="primary" /></TableCell><TableCell>{kpi.frequencia}</TableCell></TableRow>
                                            ))}</TableBody>
                                        </Table></TableContainer>
                                    </AccordionDetails>
                                </Accordion>

                                {plano.centros_afetados?.length > 0 && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="caption" color="text.secondary">Centros afetados:</Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                            {plano.centros_afetados.slice(0, 6).map((c, i) => <Chip key={i} label={c} size="small" variant="outlined" onClick={() => handleOpenDrilldown(c)} sx={{ cursor: 'pointer' }} />)}
                                            {plano.centros_afetados.length > 6 && <Chip label={`+${plano.centros_afetados.length - 6}`} size="small" />}
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        ))}

                        {(!optimizationPlan.planos || optimizationPlan.planos.length === 0) && (
                            <Alert severity="success" icon={<LightbulbIcon />}>
                                <strong>Parabéns!</strong> Não foram identificadas oportunidades críticas de otimização. Seus centros estão operando dentro dos benchmarks estabelecidos.
                            </Alert>
                        )}
                    </Stack>
                ) : <Alert severity="warning">Dados insuficientes para gerar plano de otimização.</Alert>
            )}

            {/* Tab 4: Análise de Produção */}
            {activeTab === 4 && (
                <Stack spacing={4}>
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', color: 'primary.main', mb: 2 }}>
                            <PieChartIcon sx={{ mr: 1 }} /> Insights de Produção e Rentabilidade
                        </Typography>
                        {loadingProduction ? <LinearProgress /> : productionInsights.length > 0 ? (
                            <Grid container spacing={2}>
                                {productionInsights.map((insight, idx) => (
                                    <Grid item xs={12} md={6} lg={4} key={idx}>
                                        <Paper elevation={1} sx={{ p: 2, borderLeft: 4, borderColor: insight.tipo === 'Alerta' ? 'error.main' : 'info.main', height: '100%' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                                <Typography variant="overline" color={insight.tipo === 'Alerta' ? 'error.main' : 'info.main'}>{insight.categoria}</Typography>
                                                <Chip label={insight.centro} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                                            </Stack>
                                            <Typography variant="body2" fontWeight={600} mb={1}>{insight.mensagem}</Typography>
                                            <Typography variant="caption" color="text.secondary">{insight.detalhe}</Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        ) : <Alert severity="info">Nenhum insight de produção identificado. Certifique-se de importar dados de produção com os novos campos (Médico, Especialidade, etc.).</Alert>}
                    </Paper>
                </Stack>
            )}

            {/* Tab 5: Chat */}
            {activeTab === 5 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={8}><AIInsightsWidget insights={aiInsights} /></Grid>
                    <Grid item xs={12} lg={4}><AIChatWidget /></Grid>
                </Grid>
            )}

            {/* Drill-down Modal */}
            <Dialog open={drilldownOpen} onClose={() => setDrilldownOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight="bold">Detalhes: {selectedCentro}</Typography>
                    <IconButton onClick={() => setDrilldownOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {drilldownLoading ? <LinearProgress /> : drilldownData ? (
                        <Stack spacing={3}>
                            <Grid container spacing={2}>
                                {[{ label: 'Custo', value: formatCurrency(drilldownData.totais?.custo), color: 'error' },
                                { label: 'Receita', value: formatCurrency(drilldownData.totais?.receita), color: 'success' },
                                { label: 'Margem', value: `${drilldownData.totais?.margem}%`, color: drilldownData.totais?.margem > 0 ? 'info' : 'warning' },
                                { label: 'Lançamentos', value: drilldownData.totais?.lancamentos?.toLocaleString(), color: 'grey' }
                                ].map((c, i) => (
                                    <Grid item xs={6} md={3} key={i}>
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: `${c.color}.light`, textAlign: 'center', borderRadius: 2 }}>
                                            <Typography variant="h6" fontWeight="bold">{c.value}</Typography>
                                            <Typography variant="caption">{c.label}</Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                            {drilldownData.historico?.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>Histórico</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto' }}>
                                        {drilldownData.historico.map((h, i) => {
                                            const max = Math.max(...drilldownData.historico.map(x => x.valor));
                                            return (
                                                <Tooltip key={i} title={`${h.mes}: ${formatCurrency(h.valor)}`}>
                                                    <Box sx={{ textAlign: 'center', minWidth: 35 }}>
                                                        <Box sx={{ height: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                                                            <Box sx={{ width: 18, height: `${(h.valor / max) * 100}%`, bgcolor: 'primary.main', borderRadius: '2px 2px 0 0' }} />
                                                        </Box>
                                                        <Typography variant="caption" sx={{ fontSize: '0.5rem' }}>{h.mes.slice(-5)}</Typography>
                                                    </Box>
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    ) : <Alert severity="error">Erro ao carregar.</Alert>}
                </DialogContent>
                <DialogActions><Button onClick={() => setDrilldownOpen(false)}>Fechar</Button></DialogActions>
            </Dialog>
        </Box>
    );
};

export default AIPage;
